import { DEPARTEMENTS, DEPT_WEIGHT, PERIODE_WEIGHT } from "../constants";

/**
 * Données de démonstration pour les sections non encore branchées sur l'API :
 * toutes les valeurs sont dérivées des filtres de façon déterministe.
 */

const hashOf = (str) =>
    String(str)
        .split("")
        .reduce((a, c) => a + c.charCodeAt(0), 0);

const filtersKey = (filters) =>
    `${filters.period}|${filters.agency_id ?? ""}|${filters.manager_id ?? ""}|${
        filters.department ?? ""
    }`;

/** Variation déterministe autour d'une valeur de base */
export const jit = (filters, base, amp) => {
    const s = hashOf(filtersKey(filters));
    return base + (((s % 17) - 8) / 8) * amp;
};

/* Une agence sélectionnée réduit le périmètre (poids arbitraire de démo) */
const agencyWeight = (filters) => (filters.agency_id ? 0.55 : 1);
const deptWeight = (filters) => DEPT_WEIGHT[filters.department] ?? 1;

export const scopeOf = (filters) => agencyWeight(filters) * deptWeight(filters);

/** Calcule l'ensemble des métriques affichées pour un jeu de filtres */
export const computeMetrics = (filters) => {
    const j = (base, amp) => jit(filters, base, amp);
    const s = scopeOf(filters);
    const p = PERIODE_WEIGHT[filters.period] ?? 1;

    const hc = Math.max(2, Math.round(128 * s));
    const etp = hc * 0.878;
    const effectifMoyen = hc * 0.981;

    const cdi = Math.round(hc * 0.75);
    const cdd = Math.round(hc * 0.17);
    const autre = Math.max(0, hc - cdi - cdd);

    const cadresPct = Math.round(j(34, 2));
    const tempsPleinPct = Math.round(j(78, 2));
    const cadresCount = Math.round((hc * cadresPct) / 100);
    const tempsPleinCount = Math.round((hc * tempsPleinPct) / 100);

    const entrees = Math.max(1, Math.round(19 * s * p));
    const sorties = Math.max(0, Math.round(12 * s * p));

    const deptRows = DEPARTEMENTS.filter(
        (d) => !filters.department || filters.department === d
    ).map((d) => ({
        name: d,
        value: Math.max(
            1,
            Math.round(128 * agencyWeight(filters) * DEPT_WEIGHT[d])
        ),
    }));

    const growth = p > 1 ? 1.4 : 1;
    const seriesEntrees = [3, 2, 4, 5, 2, 3].map((v) =>
        Math.max(1, Math.round(v * s * growth))
    );
    const seriesSorties = [2, 1, 3, 2, 2, 2].map((v) =>
        Math.max(0, Math.round(v * s * growth))
    );

    const joursParPeriode =
        p === 1 ? 128 : p >= 1.9 ? 243 : p <= 0.09 ? 11 : 36;
    const joursTravailles = Math.round(etp * joursParPeriode);
    const tauxAbsence = j(3.1, 0.4);
    const joursAbsence = Math.round((joursTravailles * tauxAbsence) / 100);

    const vacancesAllouees = Math.round(hc * 25);
    const vacancesDisponibles = Math.min(
        vacancesAllouees - 1,
        Math.round(1223 * s)
    );
    const vacancesConsommees = Math.max(
        0,
        vacancesAllouees - vacancesDisponibles
    );

    const heuresSup = Math.round(342 * s * p);
    const heuresTheoriques = Math.round(joursTravailles * 8.4);
    const heuresAbsence = Math.round(joursAbsence * 8.4);
    const heuresRealisees = Math.max(
        0,
        heuresTheoriques - heuresAbsence + heuresSup
    );

    const teleJours = j(3.2, 0.3);
    const telePct = Math.min(100, Math.round((teleJours / 5) * 100));

    const recrutesEssai = Math.max(1, Math.round(hc * 0.12));
    const recrutes12m = Math.max(1, Math.round(hc * 0.14));

    const turnoverVolontaire = j(7.8, 0.7);
    const turnoverInvolontaire = j(4.6, 0.5);

    const fraisTotal = 24830 * s * p;

    const scopeMin = Math.max(s, 0.3);

    return {
        scope: s,
        periodWeight: p,
        effectif: {
            headcount: hc,
            etp,
            effectifMoyen,
            evolution: j(4.9, 0.8),
            contrats: { cdi, cdd, autre },
            cadresPct,
            cadresCount,
            tempsPleinPct,
            tempsPleinCount,
            deptRows,
        },
        diversite: {
            pyramide: [
                { tranche: "55 ans +", femmes: 5, hommes: 8 },
                { tranche: "45–54", femmes: 12, hommes: 15 },
                { tranche: "35–44", femmes: 19, hommes: 22 },
                { tranche: "25–34", femmes: 18, hommes: 20 },
                { tranche: "< 25", femmes: 4, hommes: 5 },
            ],
            genre: {
                femmesPct: 44,
                hommesPct: 55,
                autrePct: 1,
                femmes: Math.round(hc * 0.44),
                hommes: Math.round(hc * 0.55),
                autre: hc - Math.round(hc * 0.44) - Math.round(hc * 0.55),
            },
            ageMoyen: j(38.6, 0.5),
            femmesManagement: j(37, 1.5),
        },
        retention: {
            tauxRetention: j(91.2, 0.8),
            confirmationEssai: j(88, 1.5),
            recrutesEssai,
            recrutementReussi: j(82, 1.5),
            recrutes12m,
            anciennete: j(4.7, 0.3),
            mobilite: j(6.4, 0.8),
        },
        mouvements: {
            entrees,
            sorties,
            tauxEntrees: (entrees / effectifMoyen) * 100,
            tauxSorties: (sorties / effectifMoyen) * 100,
            seriesEntrees,
            seriesSorties,
            labels: ["Fév", "Mar", "Avr", "Mai", "Juin", "Juil"],
            turnoverGlobal: j(12.4, 1),
            turnoverVolontaire,
            turnoverInvolontaire,
            onboardings: Math.max(1, Math.round(6 * scopeMin)),
            delaiOnboarding: j(18, 2),
            offboardings: Math.max(0, Math.round(3 * scopeMin)),
            delaiOffboarding: j(11, 1.5),
        },
        absences: {
            joursTravailles,
            joursAbsence,
            tauxActuel: tauxAbsence,
            serie: [2.6, 2.9, 2.7, 3.0, 3.2, tauxAbsence],
            dureeMoyenne: j(2.8, 0.3),
            frequence: j(0.9, 0.15),
            motifs: {
                vacances: Math.round(joursAbsence * 0.45),
                maladie: Math.round(joursAbsence * 0.3),
                accident: Math.round(joursAbsence * 0.08),
            },
        },
        temps: {
            heuresTheoriques,
            heuresRealisees,
            heuresSup,
            serieHeuresSup: [275, 290, 305, 298, 315, 289, 320, 335, heuresSup],
            teleJours,
            telePct,
        },
        engagement: {
            enps: Math.round(j(32, 4)),
            promoteurs: 48,
            detracteurs: 16,
        },
        frais: {
            total: fraisTotal,
            categories: [
                { name: "Frais véhicule", pct: 46 },
                { name: "Repas & dépl.", pct: 28 },
                { name: "Pièces & licences", pct: 18 },
                { name: "Autres", pct: 8 },
            ],
        },
        taches: {
            absencesJour: 4,
            vacancesAllouees,
            vacancesDisponibles,
            vacancesConsommees,
            onboardingEnCours: Math.max(1, Math.round(5 * scopeMin)),
            offboardingEnCours: Math.max(0, Math.round(2 * scopeMin)),
            validationFeuilles: j(94, 1.5),
            envoiFeuilles: 100 - j(8, 1),
            tachesEcheance: 7,
            tachesTotal: 15,
        },
    };
};
